import { PRIMARY_ACCOUNT_NAME, SPOUSE_PARTNER_NAME } from "../services/content/content";
import { LiabilityType } from "../types/LiabilityType";
import { RealEstateType } from "../types/RealEstateType";
import { AssetType } from "../types/AssetType";

export enum AccountRoles {
  // Add future roles related to the primary account holder here
  primaryAccountHolder = PRIMARY_ACCOUNT_NAME,
  spouse = SPOUSE_PARTNER_NAME,
}

export interface Ownable {
  ownerId?: string;
  ownerType?: string;
}

// eslint-disable-next-line complexity
export function sanitizeEstateProfileData(estateProfile: any): EstateProfile {
  if (!estateProfile) {
    throw new Error("Estate profile was null or undefined");
  }

  // Create a deep copy to avoid modifying the original object
  const sanitized = { ...estateProfile };

  // Check and sanitize array fields at the top level
  if (!sanitized.accounts) sanitized.accounts = [];
  if (!sanitized.outsideAccounts) sanitized.outsideAccounts = [];
  if (!sanitized.people) sanitized.people = [];
  if (!sanitized.manuallyEnteredOutsideAccounts) sanitized.manuallyEnteredOutsideAccounts = [];
  if (!sanitized.manuallyEnteredOutsideAssets) sanitized.manuallyEnteredOutsideAssets = [];
  if (!sanitized.manuallyEnteredLiabilities) sanitized.manuallyEnteredLiabilities = [];
  if (!sanitized.trusts) sanitized.trusts = [];

  // Sanitize nested arrays within people if they exist
  if (sanitized.people && Array.isArray(sanitized.people)) {
    sanitized.people = sanitized.people.map((person) => {
      if (person && !person.relations) {
        person.relations = [];
      }
      if (person && !person.address) {
        person.address = [];
      }
      return person;
    });
  }

  // Sanitize primaryClient if it exists
  if (sanitized.primaryClient) {
    if (!sanitized.primaryClient.relations) sanitized.primaryClient.relations = [];
    if (!sanitized.primaryClient.address) sanitized.primaryClient.address = [];
  }

  return sanitized as EstateProfile;
}

export interface EstateProfile {
  estateProfileId: string;
  primaryClient: Person;
  accounts: Account[];
  outsideAccounts: Account[];
  people?: Person[];
  manuallyEnteredOutsideAccounts: Account[];
  manuallyEnteredOutsideAssets: Asset[];
  spouse?: Person; // This is artificially created by Rob Service to easily reference the spouse data
  manuallyEnteredLiabilities: Liability[];
  trusts?: Trust[]; // Trusts created by the user
  /**
   * When explicitly `false`, Vanilla sync buttons are hidden and an unavailable message is shown.
   * When `true` or absent (`undefined`), sync buttons are displayed normally.
   * Populated from the ROB GraphQL response; may be absent for older profiles.
   */
  vanillaSyncAllowed?: boolean;
  navigationIntent?: "SHOW_CONSULTATION_PREP" | "SHOW_BREADCRUMBS";
}

export interface Person {
  piClientId?: string;
  uuid?: string;
  isSpouse?: boolean;
  firstName: string;
  lastName: string;
  // TODO: Undo the optional flag because everyone will have a relations array in the future
  relations?: Relation[];
  city?: string;
  state?: string;
  country?: string;
  zip?: string;
  address?: string[];
  dateOfBirth?: string;
  //phoneNumber?: string;
  deceased?: boolean;
}

//Person utility methods:
export function getValidPersonInfo(person?: Person): Person {
  return person || { firstName: "", lastName: "" };
}

export function getClientIdOrUuid(person?: Person): string {
  return person?.piClientId || person?.uuid || "";
}

export function getClientId(person?: Person): string {
  return person?.piClientId || "";
}

export function getUuid(person?: Person): string {
  return person?.uuid || "";
}

export interface Relation {
  to: string;
  relationshipType: string;
  relationType?: string; // Lambda outputs this field name instead of relationshipType
}

export interface NeedsAttention {
  attentionType: string; // e.g., 'TRUST_ATTENTION'
  message: string;
}

export interface MezBeneficiary {
  associatedMemberId: string;
  type: "PRIMARY" | "CONTINGENT";
  distributionPercentage: number;
}

export interface Account {
  accountId: string;
  externalId?: string;
  accountName: string;
  accountType: string;
  balance: string;
  ownerType?: string;
  ownerPoid?: string;
  ownerId?: string;
  jointType?: string;
  inEstateProfile: boolean;
  excludeFromInventory?: boolean;
  needsAttentionList?: NeedsAttention[];
  payOnDeath?: boolean;
  version?: number;
  beneficiaries?: MezBeneficiary[];
  financialInstitutionId?: string;
  financialInstitutionName?: string;
  subAccountType?: string;
}

export interface Asset {
  realEstateType?: RealEstateType;
  assetId: string;
  externalAssetId?: string;
  assetName: string;
  assetType: AssetType;
  custodianId?: string;
  ownerId: string;
  ownerType?: string;
  jointType?: string;
  isLiquid?: boolean;
  valuation: string;
  valuationDate?: string;
  inEstateProfile: boolean;
  excludeFromInventory?: boolean;
}

export interface Liability {
  liabilityId: string;
  liabilityName: string;
  liabilityType: LiabilityType;
  valuation: number;
  creditor: string;
  ownerId?: string;
  jointType?: string;
  valuationDate?: string;
  inEstateProfile: boolean;
  excludeFromInventory?: boolean;
  assetId: string;
  externalLiabilityId?: string;
  ownerType: string;
}

export interface Settlor {
  memberType: string;
  householdMemberId: string;
}

export interface Trust {
  householdMemberId?: string; // UUID from ZOK, required for UPDATE operations
  providedBy?: string; // POID that uniquely identifies this trust entity
  legalName: string | null;
  displayName: string | null;
  trustType: string; // Expected values from ROB: 'Revocable' | 'Irrevocable'
  settlors: Settlor[];
  referenceIds?: {
    clientPoid?: string;
  };
  state?: string; // State code (e.g., 'PA', 'CA')
  source?: string; // e.g., 'VANGUARD'
  isLinked?: boolean; // Indicates if trust is linked in ZOK
  needsReview?: boolean; // Indicates if trust needs review
}
